import { Chip, Paper, Portal, Typography } from "matsuri-ui"
import { Global, css } from "@emotion/react"
import { useNavigate } from "react-router-dom"
import {
    PropsWithChildren,
    createContext,
    useCallback,
    useRef,
    useState,
    Suspense,
    use,
    useTransition
} from "react"
import { Variants, motion } from "motion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useDebouncedCallback } from "use-debounce"
import { useOnClickOutside } from "matsuri-hooks"
import SearchIcon from "@mui/icons-material/Search"
import { displayOtaInfo } from "../domain/otaInfo"
import { requestGetPropertyDetail } from "../hooks/useProperty"
import {
    requestGetReservation,
    requestSearchReservationsByOtaInfo
} from "../hooks/useReservation"
import {
    requestGetRoomDetail,
    requestSearchListingsByName
} from "../hooks/useRoom"
import { requestGetRoomTypeDetail } from "../hooks/useRoomType"
import { requestSearchCommodities } from "../hooks/useCommodity"
import { theme } from "../theme"
import { validateUUIDLike } from "../helpers/validateUUIDLike"
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut"
import { QuickSearchResults } from "./QuickSearchResults"

type SearchMode = "core" | "inventory"

const styles = {
    popperVariants: {
        open: {
            opacity: 1,
            y: 0,
            clipPath: "inset(0% 0% 0% 0%)",
            transition: { type: "spring", stiffness: 300, damping: 24 }
        },
        closed: {
            opacity: 0,
            y: -10,
            clipPath: "inset(0% 0% 90% 0%)",
            transition: { duration: 0.2 }
        }
    } as Variants
}

interface QuickSearchContextType {
    launch: () => void
    close: () => void
}

const QuickSearchContext = createContext<QuickSearchContextType | null>(null)

export const useQuickSearch = () => {
    const context = use(QuickSearchContext)
    if (!context) {
        throw new Error(
            "useQuickSearch must be used within QuickSearchProvider"
        )
    }
    return context
}

interface QuickSearchProps {
    mode: SearchMode
    onChangeMode: (mode: SearchMode) => void
    label?: string
    placeholder?: string
    open?: boolean
    onClose: () => void
    token: string
}

const QuickSearchWindow = ({
    open,
    mode,
    onChangeMode,
    label,
    placeholder,
    onClose,
    token
}: QuickSearchProps) => {
    return open ? (
        <QuickSearchWindowInner
            mode={mode}
            onChangeMode={onChangeMode}
            label={label}
            placeholder={placeholder}
            onClose={onClose}
            token={token}
        />
    ) : null
}

// openしていない時にkeyboard handlerが登録されないようにコンポーネントを分けている
const QuickSearchWindowInner = ({
    mode,
    onChangeMode,
    label,
    placeholder,
    onClose,
    token
}: Omit<QuickSearchProps, "open">) => {
    const navigate = useNavigate()
    const [searchKeyword, setSearchKeyword] = useState("")
    const [searchPromise, setSearchPromise] = useState<Promise<
        { label: string; url: string; tags: string[] }[]
    > | null>(null)

    const handleNavigate = (url: string) => {
        navigate(url)
        onClose()
    }

    const createSearchPromise = useCallback(
        async (value: string) => {
            const promises: (() => Promise<
                { label: string; url: string; tags: string[] }[]
            >)[] = []

            // 両端の空白をtrim
            const trimedValue = value.trim()
            const isUUIDLike = validateUUIDLike(trimedValue)

            promises.push(async () => {
                const { data } = await requestSearchListingsByName(token, {
                    name: trimedValue,
                    itemsPerPage: 200,
                    page: 0
                })

                return (
                    data?.listings?.map(listing => ({
                        label: listing.name,
                        url: `/listing/${listing.id}`,
                        tags: ["物件"]
                    })) ?? []
                )
            })

            if (mode === "core") {
                const isOtaIdLike = /^[A-Za-z0-9_]+$/.exec(trimedValue)

                if (trimedValue && isOtaIdLike) {
                    promises.push(async () => {
                        const { data } =
                            await requestSearchReservationsByOtaInfo(token, {
                                otaId: trimedValue
                            })

                        return (
                            data?.reservations?.map(reservation => ({
                                label: `${displayOtaInfo(
                                    reservation.otaInfo
                                )} - ${trimedValue}`,
                                url: `/reservation/${reservation.id}`,
                                tags: ["予約"]
                            })) ?? []
                        )
                    })
                }

                if (trimedValue && isUUIDLike) {
                    promises.push(async () => {
                        const { data } = await requestGetReservation(
                            token,
                            trimedValue
                        )

                        return data
                            ? [
                                  {
                                      label: `${displayOtaInfo(
                                          data.reservation.otaInfo
                                      )} - ${data.reservation.otaInfo.otaId}`,
                                      url: `/reservation/${data.reservation.id}`,
                                      tags: ["予約"]
                                  }
                              ]
                            : []
                    })

                    promises.push(async () => {
                        const { data } = await requestGetRoomDetail(
                            token,
                            trimedValue
                        )

                        return data
                            ? [
                                  {
                                      label: `${data.listing.name}`,
                                      url: `/listing/${data.listing.id}`,
                                      tags: ["物件"]
                                  }
                              ]
                            : []
                    })

                    promises.push(async () => {
                        const { data } = await requestGetRoomTypeDetail(
                            token,
                            trimedValue
                        )

                        return data
                            ? [
                                  {
                                      label: `${data.name}`,
                                      url: `/roomType/${data.id}`,
                                      tags: ["部屋タイプ"]
                                  }
                              ]
                            : []
                    })

                    promises.push(async () => {
                        const { data } = await requestGetPropertyDetail(
                            token,
                            trimedValue
                        )

                        return data
                            ? [
                                  {
                                      label: `${data.name}`,
                                      url: `/property/${data.id}`,
                                      tags: ["施設"]
                                  }
                              ]
                            : []
                    })
                }
            }

            if (mode === "inventory") {
                const isManagementIdLike = /^\d+$/.exec(trimedValue)

                if (trimedValue) {
                    promises.push(async () => {
                        const { data } = await requestSearchCommodities(token, {
                            conditions: {
                                nameQuery: trimedValue
                            },
                            pagination: {
                                page: 0,
                                itemsPerPage: 100
                            }
                        })

                        return (
                            data?.data?.map(commodity => ({
                                label: `${commodity.name} (${commodity.managementId})`,
                                url: `/inventory/commodity/${commodity.id}`,
                                tags: ["物品"]
                            })) ?? []
                        )
                    })
                }

                if (trimedValue && isManagementIdLike) {
                    promises.push(async () => {
                        const { data } = await requestSearchCommodities(token, {
                            conditions: {
                                managementId: trimedValue
                            },
                            pagination: {
                                page: 0,
                                itemsPerPage: 1
                            }
                        })

                        return (
                            data?.data?.map(commodity => ({
                                label: `${commodity.name} (${commodity.managementId})`,
                                url: `/inventory/commodity/${commodity.id}`,
                                tags: ["物品"]
                            })) ?? []
                        )
                    })
                }
            }

            const results = await Promise.all(promises.map(p => p()))
            return results.flat()
        },
        [mode, token]
    )

    const [isPending, startTransition] = useTransition()
    const debouncedOnChange = useDebouncedCallback((value: string) => {
        setSearchKeyword(value)
        if (value.trim()) {
            startTransition(() => {
                setSearchPromise(createSearchPromise(value))
            })
        } else {
            setSearchPromise(null)
        }
    }, 200)

    const ref = useRef<HTMLDivElement>(null)
    useOnClickOutside(ref, onClose)

    const currentSearchWordRef = useRef<string>("")

    return (
        <div
            css={css`
                position: fixed;
                top: 0;
                left: 0;
                z-index: ${theme.zIndex.modal};
                display: grid;
                place-items: center;
                width: 100%;
                height: 100%;
                padding: 16px;

                background-color: rgba(0, 0, 0, 0.75);
            `}
        >
            <Global
                styles={css`
                    // このコンポーネントが開いている間はスクロールを禁止する
                    body {
                        overflow: hidden;
                    }
                `}
            />
            <div
                css={css`
                    display: grid;
                    gap: 4px;
                    width: min(550px, 100%);
                `}
            >
                <Typography
                    css={css`
                        color: white;
                    `}
                >
                    {label}
                </Typography>
                <Paper
                    css={css`
                        display: grid;
                        flex: 1;
                        grid-template-rows: auto auto 450px;
                        gap: 16px;
                        padding-top: 8px;
                    `}
                    ref={ref}
                >
                    <form
                        css={css`
                            display: grid;
                            grid-template-columns: auto 1fr;
                            gap: 8px;
                            padding: 0 16px;
                        `}
                        onSubmit={e => {
                            e.preventDefault()
                        }}
                    >
                        <div
                            css={css`
                                display: grid;
                                place-items: center;
                                font-size: 20px;
                            `}
                        >
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            css={[
                                theme.typography.body,
                                css`
                                    padding: 8px 0;
                                    font-size: 20px;
                                `
                            ]}
                            placeholder={placeholder}
                            autoFocus
                            onChange={event => {
                                const value = event.currentTarget.value

                                debouncedOnChange(value)
                                currentSearchWordRef.current = value
                            }}
                        />
                    </form>
                    <div
                        css={css`
                            display: flex;
                            gap: 8px;
                            padding: 0 16px;
                        `}
                    >
                        {(["core", "inventory"] as SearchMode[]).map(
                            modeName => (
                                <Chip
                                    role="button"
                                    key={modeName}
                                    color={
                                        mode === modeName
                                            ? "primary"
                                            : "default"
                                    }
                                    onClick={() => {
                                        onChangeMode(modeName)
                                        debouncedOnChange(
                                            currentSearchWordRef.current
                                        )
                                    }}
                                    css={css`
                                        cursor: pointer;
                                    `}
                                >
                                    {modeName}を検索
                                </Chip>
                            )
                        )}
                    </div>
                    <motion.div
                        initial="closed"
                        animate={searchPromise ? "open" : "closed"}
                    >
                        <div
                            css={css`
                                display: grid;
                                align-items: flex-start;
                                max-height: 100%;
                                overflow-y: scroll;
                            `}
                        >
                            <motion.div
                                variants={styles.popperVariants}
                                css={css`
                                    display: grid;
                                    align-items: flex-start;
                                    height: 100%;
                                `}
                            >
                                {searchPromise && (
                                    <Suspense
                                        key={searchKeyword}
                                        fallback={
                                            <div
                                                css={css`
                                                    padding: 16px;
                                                `}
                                            >
                                                検索中...
                                            </div>
                                        }
                                    >
                                        <div
                                            css={[
                                                css`
                                                    transition: opacity 0.2s;
                                                `,
                                                isPending
                                                    ? css`
                                                          opacity: 0.5;
                                                      `
                                                    : null
                                            ]}
                                        >
                                            <QuickSearchResults
                                                searchPromise={searchPromise}
                                                onClose={onClose}
                                                onNavigate={handleNavigate}
                                            />
                                        </div>
                                    </Suspense>
                                )}
                            </motion.div>
                        </div>
                    </motion.div>
                </Paper>
            </div>
        </div>
    )
}

export const QuickSearchProvider = ({ children }: PropsWithChildren) => {
    const { authenticated, token } = useAuth()
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState<SearchMode>("core")

    const getLaunchMode = useCallback(() => {
        return document.location.pathname.startsWith("/inventory")
            ? ("inventory" as const)
            : ("core" as const)
    }, [])

    const launch = useCallback(() => {
        setMode(getLaunchMode())
        setOpen(true)
    }, [getLaunchMode])
    const close = useCallback(() => setOpen(false), [])

    // Ctrl+K shortcut
    useKeyboardShortcut(
        {
            combo: { key: "k", modifiers: ["ctrl"] },
            handler: launch,
            preventDefault: true,
            ignoreInputs: true
        },
        { enabled: authenticated }
    )

    // Double Shift shortcut
    useKeyboardShortcut(
        {
            sequence: ["Shift", "Shift"],
            handler: launch,
            ignoreInputs: true
        },
        { enabled: authenticated, sequenceTimeout: 500 }
    )

    // Escape to close (only when open)
    useKeyboardShortcut(
        {
            combo: { key: "Escape" },
            handler: close
        },
        { enabled: open && authenticated }
    )

    if (!authenticated) {
        return <>{children}</>
    }

    const contextValue = { launch, close }

    return (
        <QuickSearchContext value={contextValue}>
            {children}
            <Portal>
                <QuickSearchWindow
                    mode={mode}
                    onChangeMode={setMode}
                    label="QuickSearch (Ctrl + K または Shift2回)"
                    placeholder={
                        mode === "core"
                            ? "物件名・予約コードなどで検索"
                            : "物品名・管理番号などで検索"
                    }
                    open={open}
                    token={token}
                    onClose={close}
                />
            </Portal>
        </QuickSearchContext>
    )
}
